import { PersonalNetWorthRange, UpdateProfileStatus, useCustomerDetails, useUpdateFinancialInfo } from '@egw/api'
import { Button, Field, ModalDrawer, RadioGroup, useModalState } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Controller, useForm } from 'react-hook-form'
import { Logger } from '@egw/logger'
import { InformationCircleIcon } from '@heroicons/react/16/solid'
import { useEffect, useState, type FC } from 'react'
import { FinancialField } from '../../types'

const log = new Logger('Net Worth')

interface NetWorthFormInterface {
  [FinancialField.PERSONAL_NET_WORTH_RANGE]: PersonalNetWorthRange
}

export const EditNetWorthField: FC = () => {
  const { t } = useTranslation('features-settings')
  const { isOpen, openModal, closeModal } = useModalState()

  const [updateError, setUpdateError] = useState<boolean>(false)

  const { data, refetch, isRefetching } = useCustomerDetails()

  const value = data?.customer.personalNetWorthRange

  const { mutate: updateFinancial, isLoading } = useUpdateFinancialInfo({
    onSuccess: (res) => {
      if (res.__typename === 'UpdateProfileError' || res.status === UpdateProfileStatus.FAILED) {
        log.error('Error updating networth', { data })
        setUpdateError(true)
      } else if (res.status === UpdateProfileStatus.COMPLETE) {
        void refetch()
          .catch((error: unknown) => {
            log.error(error)
          })
          .finally(() => {
            setUpdateError(false)
            closeModal()
            reset()
          })
      }
    },
    onError: () => {
      setUpdateError(true)
    },
  })

  const defaultValues = {
    [FinancialField.PERSONAL_NET_WORTH_RANGE]: value,
  }

  const { handleSubmit, control, reset, formState } = useForm<NetWorthFormInterface>({
    mode: 'all',
    defaultValues,
  })

  useEffect(() => {
    if (data) {
      reset({
        [FinancialField.PERSONAL_NET_WORTH_RANGE]: value,
      })
    }
  }, [data, reset, value])

  const onSubmit = async (formData: any) => {
    setUpdateError(false)
    log.debug('Submitting Form', { formData })
    void updateFinancial(formData)
  }

  return value !== undefined ? (
    <>
      <Field
        valueClassName="font-light"
        className="w-full justify-between border-list mb-0 py-4 px-2"
        onEdit={openModal}
        value={t('financial_labels.net_worth')}
        rightValue={t(`personal_net_worth_range.${value}`)}
        isPII
      />
      <ModalDrawer title={t('financial_labels.net_worth')} isOpen={isOpen} onClose={closeModal}>
        <form
          id="updateNetWorthForm"
          className="flex flex-col gap-4 h-full md:h-screen justify-between"
          onSubmit={(e) => {
            e.preventDefault()
            void handleSubmit(onSubmit)()
          }}
        >
          <div className="flex flex-col gap-10">
            <div>
              <p className="mb-sm font-semibold">{t('update_net_worth.your_net_worth')}</p>
              <Controller
                name={FinancialField.PERSONAL_NET_WORTH_RANGE}
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    {...field}
                    value={field.value}
                    options={Object.values(PersonalNetWorthRange).map((item) => {
                      return {
                        label: t(`personal_net_worth_range.${item}`),
                        value: item,
                      }
                    })}
                    isPII
                  />
                )}
              />
            </div>
            {updateError ? (
              <div className="flex bg-alertErrorBg p-3 rounded-md">
                <InformationCircleIcon className="w-6 h-6 fill-red-500" />
                <p className="text-sm text-alertErrorText ml-2">{t('common.update_error')} </p>
              </div>
            ) : null}
          </div>
          <Button
            id="updateNetWorth"
            type="submit"
            isLoading={isLoading || isRefetching}
            isDisabled={!formState.isValid}
            className="mt-4 md:mt-0"
          >
            {t('common.confirm')}
          </Button>
        </form>
      </ModalDrawer>
    </>
  ) : null
}
